import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Button, Tabs, Space, Typography } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import type { TabsProps } from 'antd';
import './MainContent.css'; // 引入样式文件
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createGeminiSummaryJob, endGeminiMerge, getFileStatus, getGeminiFile, getLogDetail } from '@/api/subtitles';
import RealTimeContent from './RealTimeContent';
import GeminiSummaryContent, { GeminiGenerateType } from './GeminiSummaryContent';
import MeetStatus, { IMeetStatus } from './MeetStatus';
import { trackEvent, TrackingEvent, TabName } from '@/utils/tracking';
import AISidebar from '@/pages/record/aisidebar';

const LANGUAGE_MAP = {
  zh_hans: '中文',
  en: 'English',
  ja: '日本語',
} as const;

type LanguageKey = keyof typeof LANGUAGE_MAP;

const MainContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subtitle_segment_id: subtitleSegmentId } = useParams();
  const { Header, Content } = Layout;
  const [currentLang, setCurrentLang] = useState<LanguageKey>('zh_hans');
  const [roomId, setRoomId] = useState('');
  const { Link } = Typography;
  const [meetStatus, setMeetStatus] = useState<IMeetStatus>('UNKNOWN');
  const [isFinish, setIsFinish] = useState(false);
  const [title, setTitle] = useState('');
  const [gmtCreate, setGmtCreate] = useState('');
  const [historyData, setHistoryData] = useState<API.SubtitleItem[]>([]);
  const [fileMergeId, setFileMergeId] = useState('');
  const [summaryEmpty, setSummaryEmpty] = useState(false);

  const init = useCallback(async () => {
    if (!subtitleSegmentId) return;

    try {
      const res = await getLogDetail(subtitleSegmentId);
      setTitle(res.data.data.title);
      setGmtCreate(res.data.data.gmt_create);
      setHistoryData(res.data.data.subtitle_list);
      setRoomId(res.data.data.room_id);
    } catch (err: any) {
      console.error('Failed to get log detail:', err);
      if (err?.response?.status === 404) {
        setMeetStatus('NO_ACCESS');
      }
    }
  }, [subtitleSegmentId]);

  const getContentStatus = async (fileId: string): Promise<IMeetStatus> => {
    try {
      const {
        data: {
          data: { status },
        },
      } = await getFileStatus({ file_merge_id: fileId });
      return status as IMeetStatus;
    } catch (err) {
      console.error('Failed to get content status:', err);
      return 'ERROR';
    }
  };

  const getHistoryData = useCallback(async (newRoomId: string) => {
    try {
      const {
        data: {
          data: { file_merge_id },
        },
      } = await getGeminiFile({ roomId: newRoomId });
      setFileMergeId(file_merge_id);

      const status = await getContentStatus(file_merge_id);
      setMeetStatus(status);
      setIsFinish(status === 'FINISH');
    } catch (err) {
      console.error('Failed to get history data:', err);
      setMeetStatus('ERROR');
    }
  }, []);

  useEffect(() => {
    if (roomId) {
      getHistoryData(roomId);
    }
  }, [roomId, getHistoryData]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    const storedLang = localStorage.getItem('preferred_language') as LanguageKey;
    if (storedLang && LANGUAGE_MAP[storedLang]) {
      setCurrentLang(storedLang);
    }
  }, []);

  const handleEndMeeting = useCallback(async () => {
    try {
      const endMergeResponse = await endGeminiMerge(roomId);
      if (endMergeResponse?.data?.status !== 200) return;

      await createGeminiSummaryJob(fileMergeId, currentLang);
      setMeetStatus('WAIT_DO');
    } catch (err) {
      console.error('Failed to end meeting:', err);
    }
  }, [roomId, fileMergeId, currentLang]);

  const tabs = useMemo(
    () => [
      {
        key: 'summary',
        label: t('meeting.meetingSummary'),
        children: (
          <GeminiSummaryContent
            isFinish={isFinish}
            meetStatus={meetStatus}
            handleEndMeeting={handleEndMeeting}
            fileMergeId={fileMergeId}
            generateType={GeminiGenerateType.SUMMARY}
            targetLang={currentLang}
            onSummaryEmpty={() => setSummaryEmpty(true)}
          />
        ),
      },
      {
        key: 'original',
        label: t('meeting.realTimeTranscription'),
        children:
          meetStatus === 'NO_ACCESS' ? (
            <MeetStatus meetStatus={meetStatus} contentType="transcription" />
          ) : (
            <RealTimeContent roomId={roomId} historyData={historyData} />
          ),
      },
    ],
    [meetStatus, isFinish, fileMergeId, currentLang, roomId, historyData, handleEndMeeting, t],
  );

  // Tab 切换埋点
  const handleTabChange = useCallback((activeKey: string) => {
    let tabName: TabName;
    switch (activeKey) {
      case 'summary':
        tabName = TabName.SUMMARY;
        break;
      case 'transcription':
      case 'original':
        tabName = TabName.TRANSCRIPT;
        break;
      case 'translation':
        tabName = TabName.TRANSLATION;
        break;
      default:
        return;
    }
    trackEvent(TrackingEvent.TAB_CLICK, { tab_name: tabName });
  }, []);

  // 返回按钮点击埋点
  const handleBackClick = useCallback(() => {
    trackEvent(TrackingEvent.BACK_CLICK, {});
    navigate('/record');
  }, [navigate]);

  return (
    <Layout style={{ background: '#fff' }}>
      <Header
        style={{
          background: '#fff',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <Space>
          <Button className="analyse-page-btn" icon={<ArrowLeftOutlined />} type="text" onClick={handleBackClick} />
          <h2 className="analyse-page-title" title={title} style={{ margin: 0, color: '#333333' }}>
            {title}
          </h2>
        </Space>
        <Space style={{ display: 'none' }}>
          <Link
            className="analyse-page-btn"
            onClick={() => {
              /* TODO: 实现下载逻辑 */
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              color: '#8c8c8c',
            }}
          >
            <DownloadOutlined style={{ marginRight: 4 }} />
            {t('meeting.downloadRecord')}
          </Link>
        </Space>
      </Header>
      <Content
        style={{
          padding: '12px',
          maxWidth: '800px',
        }}
      >
        <Tabs defaultActiveKey="summary" items={tabs as TabsProps['items']} onChange={handleTabChange} />
      </Content>

      {meetStatus === 'FINISH' && !summaryEmpty && (
        <AISidebar
          segmentId={subtitleSegmentId}
          fileMergeId={fileMergeId}
          title={title}
          gmtCreate={gmtCreate}
        />
      )}
    </Layout>
  );
};

export default MainContent;
